import { useEffect, useMemo } from 'react';
import { GridApi, GridValidRowModel } from '@mui/x-data-grid-premium';
import { MutableRefObject } from 'react';

const ACTION_STATUSES = ['N', 'C', 'E', 'D'] as const;

type ActionStatus = (typeof ACTION_STATUSES)[number];

type ActionRow = GridValidRowModel & {
  status?: ActionStatus;
};

type IsDeletionRequestedRow<T extends ActionRow> = (params: { row: T }) => boolean;

export function usePinnedActionColumn<T extends ActionRow>({
  rows,
  apiRef,
  setNewRows,
  setIsDeletionRequestedRow,
}: {
  rows: T[];
  apiRef: MutableRefObject<GridApi>;
  setNewRows: (rows: T[]) => void;
  setIsDeletionRequestedRow?: IsDeletionRequestedRow<T>;
}) {
  const hasActionRows = useMemo(() => {
    return rows.some(
      (r) =>
        (r.status ? ACTION_STATUSES.includes(r.status) : false) ||
        !!setIsDeletionRequestedRow?.({ row: r }),
    );
  }, [rows, setIsDeletionRequestedRow]);

  useEffect(() => {
    setNewRows([...rows]);

    if (!apiRef?.current) return;

    const updateGrid = () => {
      const leftPinned = hasActionRows ? ['undoDelete'] : [];
      apiRef.current.setPinnedColumns({ left: leftPinned });
      apiRef.current.resize();
    };

    const timeout = setTimeout(updateGrid, 0);
    return () => clearTimeout(timeout);
  }, [rows, hasActionRows, apiRef, setNewRows]);

  return { hasActionRows };
}
